import { deepClone } from './deep-clone';

/**
 * @description Merge object 2 into object 1 recursively.
 * This differs from Object.assign() in that child objects are not references to the original.
 * Child objects / arrays that are present in each object are merged.
 * Returns the result, treats object 1 / 2 as immutable since there is not guarantee that they are not.
 *
 * @link [Codepen Playground](https://codepen.io/agentender/pen/abNVxvV)
 * @example
 * ```
 *      Object A ({
 *          "auth0": {
 *          "clientId": "XXX-YYY-ZZZ",
 *              "audience": "COMMON AUD"
 *          },
 *          "urls": {
 *              "API1": "ENV_API1.AZUREWEBSITES.NET"
 *          }
 *      })
 *      Object B ({
 *          "auth0": {
 *              "clientId": "SPECIFIC ID"
 *          },
 *          "urls": {
 *              "PROJECT_SPECIFIC_URL": "TEST"
 *          }
 *      })
 *      deepMerge(A, B) ({
 *          "auth0": {
 *              "clientId": "SPECIFIC ID",
 *              "audience": "COMMON AUD"
 *          },
 *          "urls": {
 *              "API1": "ENV_API1.AZUREWEBSITES.NET",
 *              "PROJECT_SPECIFIC_URL": "TEST"
 *          }
 *      })
 * ```
 */
export function deepMerge<T1 extends object, T2 extends object>(
  obj1: T1,
  obj2: T2
): T1 & T2 {
  const result: Record<string, unknown> = {};
  if (obj1 === null) {
    return (obj2 === null ? null : deepClone(obj2)) as T1 & T2;
  }
  const obj1Record = obj1 as Record<string, unknown>;
  const obj2Record = obj2 as Record<string, unknown>;

  Object.entries(obj1Record).forEach(([key, value]) => {
    if (key in obj2Record) {
      // potential overwrite
      if (typeof value !== typeof obj2Record[key]) {
        // value type mismatch, always take obj2's values.
        result[key] = obj2Record[key];
      } else if (typeof value === 'object' && value !== null) {
        result[key] = deepMerge(
          value as Record<string, unknown>,
          obj2Record[key] as Record<string, unknown>
        );
      } else {
        result[key] = obj2Record[key];
      }
    } else {
      result[key] = value;
    }
  });
  Object.entries(obj2Record)
    .filter(([key]) => !(key in obj1Record))
    .forEach(([key, value]) => {
      result[key] = value;
    });
  return result as T1 & T2;
}
